import { applyDesignerPropertyChange } from "../../designer-property-actions";
import { getSdInstance } from "../helpers";
import {
  getComponentDef,
  getCategoryGroup,
  applyCssStylesToComponent,
  getContrastRatio,
  parseColor,
  resolveEffectiveBackground,
  findAccessibleForeground
} from "../helpers/style-helpers";
import type { ToolHandlerGroup } from "../types";

function safeParse(val: any): any {
  if (val == null) return val;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
  return val;
}

interface ComponentInfo {
  kuikaId: string;
  name: string;
  friendlyName: string | null;
  componentCategory: string | null;
  categoryGroup: string;
  parentId: string | null;
  depth: number;
  styleObj: any;
  properties: any[];
}

function collectComponents(di: any, sd: any, depth: number, results: ComponentInfo[]): void {
  const componentDef = getComponentDef(sd, di);
  const componentCategory = componentDef?.componentCategory || null;

  results.push({
    kuikaId: di.id?.toString(),
    name: di.item?.name || "Unknown",
    friendlyName: di.friendlyName || null,
    componentCategory,
    categoryGroup: getCategoryGroup(componentCategory),
    parentId: di.parentId != null ? di.parentId.toString() : null,
    depth,
    styleObj: di.styleObj || null,
    properties: di.item?.properties || []
  });

  if (di.children) {
    for (const child of di.children) {
      collectComponents(child, sd, depth + 1, results);
    }
  }
}

interface ReviewIssue {
  category: string;
  severity: "error" | "warning" | "info";
  issue: string;
  components: string[];
  details?: any;
  suggestion?: string;
}

interface QuickFix {
  description: string;
  components: string[];
  styles?: Record<string, any>;
  properties?: Array<{ propertyName: string; value: string }>;
  accessibilityInfo?: {
    oldRatio: number;
    newRatio: number;
    standard: string;
  };
}

function isTranslucent(color: string): boolean {
  const parsed = parseColor(color);
  if (!parsed) return false;
  return parsed[3] < 1;
}

function checkSpacingConsistency(components: ComponentInfo[]): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  // Group components by parent
  const byParent = new Map<string, ComponentInfo[]>();
  for (const comp of components) {
    if (comp.parentId) {
      const siblings = byParent.get(comp.parentId) || [];
      siblings.push(comp);
      byParent.set(comp.parentId, siblings);
    }
  }

  for (const [, siblings] of byParent) {
    // Check padding consistency among siblings of same category
    const containerSiblings = siblings.filter((s) => s.categoryGroup === "container");
    if (containerSiblings.length >= 2) {
      const paddings = containerSiblings
        .map((s) => {
          const pos = s.styleObj?.position;
          if (!pos) return null;
          return {
            kuikaId: s.kuikaId,
            top: pos.paddingTop ?? 0,
            right: pos.paddingRight ?? 0,
            bottom: pos.paddingBottom ?? 0,
            left: pos.paddingLeft ?? 0
          };
        })
        .filter(Boolean) as any[];

      if (paddings.length >= 2) {
        const uniquePaddings = new Set(paddings.map((p: any) => `${p.top},${p.right},${p.bottom},${p.left}`));
        if (uniquePaddings.size > 1) {
          // Find most common padding
          const paddingCounts = new Map<string, number>();
          for (const p of paddings) {
            const key = `${p.top},${p.right},${p.bottom},${p.left}`;
            paddingCounts.set(key, (paddingCounts.get(key) || 0) + 1);
          }
          let bestPadding = "";
          let bestCount = 0;
          for (const [key, count] of paddingCounts) {
            if (count > bestCount) {
              bestPadding = key;
              bestCount = count;
            }
          }
          const [sugT, sugR, sugB, sugL] = bestPadding.split(",").map(Number);
          const nonConforming = paddings
            .filter((p: any) => `${p.top},${p.right},${p.bottom},${p.left}` !== bestPadding)
            .map((p: any) => p.kuikaId);

          if (nonConforming.length > 0) {
            issues.push({
              category: "spacing",
              severity: "warning",
              issue: "inconsistent_padding",
              components: nonConforming,
              details: { uniqueValues: uniquePaddings.size, suggested: bestPadding },
              suggestion: `Normalize padding to ${sugT}/${sugR}/${sugB}/${sugL} across sibling containers`
            });

            fixes.push({
              description: "Normalize container padding",
              components: nonConforming,
              styles: { paddingTop: sugT, paddingRight: sugR, paddingBottom: sugB, paddingLeft: sugL }
            });
          }
        }
      }
    }
  }

  // Check components with background but no padding
  for (const comp of components) {
    const fillColor = comp.styleObj?.fill?.color;
    const parsedFill = fillColor ? parseColor(fillColor) : null;
    const isVisibleBackground = fillColor && fillColor !== "transparent" && (!parsedFill || parsedFill[3] > 0);
    if (isVisibleBackground) {
      const pos = comp.styleObj?.position;
      const top = pos?.paddingTop ?? 0;
      const right = pos?.paddingRight ?? 0;
      const bottom = pos?.paddingBottom ?? 0;
      const left = pos?.paddingLeft ?? 0;

      if (top === 0 && right === 0 && bottom === 0 && left === 0) {
        issues.push({
          category: "spacing",
          severity: "warning",
          issue: "background_no_padding",
          components: [comp.kuikaId],
          details: { fillColor },
          suggestion: `${comp.name} ${comp.friendlyName || comp.kuikaId} has a background color but no padding. Add padding for better readability.`
        });

        fixes.push({
          description: `Add padding to ${comp.name} ${comp.friendlyName || comp.kuikaId} with background`,
          components: [comp.kuikaId],
          styles: { paddingTop: 8, paddingRight: 16, paddingBottom: 8, paddingLeft: 16 }
        });
      }
    }
  }

  // borderRadius consistency: only flag if design uses rounded corners (majority have radius)
  // but some backgrounded components are missing it
  const bgComponents = components.filter((c) => {
    const fc = c.styleObj?.fill?.color;
    const parsed = fc ? parseColor(fc) : null;
    return fc && fc !== "transparent" && (!parsed || parsed[3] > 0);
  });
  if (bgComponents.length >= 2) {
    const withRadius = bgComponents.filter((c) => {
      const b = c.styleObj?.border;
      return (b?.borderTopLeftRadius ?? 0) > 0 || (b?.borderBottomLeftRadius ?? 0) > 0;
    });
    const withoutRadius = bgComponents.filter((c) => {
      const b = c.styleObj?.border;
      return (b?.borderTopLeftRadius ?? 0) === 0 && (b?.borderBottomLeftRadius ?? 0) === 0;
    });
    // If majority use borderRadius, flag the ones missing it
    if (withRadius.length > withoutRadius.length && withoutRadius.length > 0) {
      // Use the most common radius value as the fix
      const radiusCounts = new Map<number, number>();
      for (const c of withRadius) {
        const r = c.styleObj?.border?.borderTopLeftRadius ?? 0;
        radiusCounts.set(r, (radiusCounts.get(r) || 0) + 1);
      }
      let commonRadius = 8;
      let maxCount = 0;
      for (const [r, count] of radiusCounts) {
        if (count > maxCount) {
          commonRadius = r;
          maxCount = count;
        }
      }
      for (const comp of withoutRadius) {
        issues.push({
          category: "spacing",
          severity: "warning",
          issue: "inconsistent_border_radius",
          components: [comp.kuikaId],
          details: { expectedRadius: commonRadius },
          suggestion: `${comp.friendlyName || comp.name} has backgroundColor but no borderRadius — ${withRadius.length} other components use borderRadius ${commonRadius}px`
        });
        fixes.push({
          description: `Add borderRadius ${commonRadius}px to ${comp.friendlyName || comp.name} for consistency`,
          components: [comp.kuikaId],
          styles: { borderRadius: commonRadius }
        });
      }
    }
  }

  return { issues, fixes };
}

function checkTypography(components: ComponentInfo[]): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  const textComponents = components.filter(
    (c) => c.categoryGroup === "display" || c.name === "Label" || c.name === "Button"
  );

  const fontSizes = new Set<number>();
  for (const comp of textComponents) {
    const fontSize = comp.styleObj?.text?.fontSize;
    if (fontSize != null) fontSizes.add(fontSize);
  }

  if (fontSizes.size > 5) {
    issues.push({
      category: "typography",
      severity: "warning",
      issue: "too_many_font_sizes",
      components: textComponents.map((c) => c.kuikaId),
      details: { count: fontSizes.size, sizes: [...fontSizes].sort((a, b) => a - b) },
      suggestion: `Using ${fontSizes.size} different font sizes. Consider consolidating to 3-4 sizes for a cleaner hierarchy.`
    });
  }

  // Check for sibling style inconsistency — Labels at the same depth with same fontSize
  // but different fontWeight/color/lineHeight suggest an accidental styling gap.
  const byParentAndSize = new Map<string, ComponentInfo[]>();
  for (const comp of textComponents) {
    const fs = comp.styleObj?.text?.fontSize;
    if (fs != null && comp.parentId != null) {
      // Group by grandparent (card level) + fontSize to find siblings with same role
      const key = `${comp.depth}_${fs}`;
      const arr = byParentAndSize.get(key) || [];
      arr.push(comp);
      byParentAndSize.set(key, arr);
    }
  }
  for (const [, siblings] of byParentAndSize) {
    if (siblings.length < 2) continue;
    // Check if any sibling is missing fontWeight or color that others have
    const withWeight = siblings.filter((s) => s.styleObj?.text?.fontWeight != null);
    const withColor = siblings.filter((s) => s.styleObj?.text?.color != null);
    if (withWeight.length > 0 && withWeight.length < siblings.length) {
      const missing = siblings.filter((s) => s.styleObj?.text?.fontWeight == null);
      const referenceWeight = withWeight[0].styleObj.text.fontWeight;
      const referenceColor = withWeight[0].styleObj?.text?.color;
      for (const comp of missing) {
        const fixStyles: Record<string, any> = { fontWeight: referenceWeight };
        if (referenceColor && !comp.styleObj?.text?.color) {
          fixStyles.color = referenceColor;
        }
        issues.push({
          category: "typography",
          severity: "warning",
          issue: "inconsistent_sibling_styles",
          components: [comp.kuikaId],
          details: { missing: "fontWeight", reference: withWeight[0].kuikaId },
          suggestion: `${comp.friendlyName || comp.name} is missing fontWeight that sibling ${withWeight[0].friendlyName || withWeight[0].name} has (${referenceWeight})`
        });
        fixes.push({
          description: `Match sibling styles on ${comp.friendlyName || comp.name}`,
          components: [comp.kuikaId],
          styles: fixStyles
        });
      }
    }
  }

  // Check for very small text
  for (const comp of textComponents) {
    const fontSize = comp.styleObj?.text?.fontSize;
    if (fontSize != null && fontSize < 12) {
      issues.push({
        category: "typography",
        severity: "warning",
        issue: "small_text",
        components: [comp.kuikaId],
        details: { fontSize },
        suggestion: `Font size ${fontSize}px is below recommended minimum of 12px`
      });
      fixes.push({
        description: `Increase font size to 12px on ${comp.friendlyName || comp.name}`,
        components: [comp.kuikaId],
        styles: { fontSize: 12 }
      });
    }
  }

  return { issues, fixes };
}

function checkShadowConsistency(components: ComponentInfo[]): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  const containers = components.filter((c) => c.categoryGroup === "container" && c.name === "Panel");

  if (containers.length < 2) return { issues, fixes };

  // Group by depth to compare same-level panels
  const byDepth = new Map<number, ComponentInfo[]>();
  for (const comp of containers) {
    const group = byDepth.get(comp.depth) || [];
    group.push(comp);
    byDepth.set(comp.depth, group);
  }

  for (const [_depth, peers] of byDepth) {
    if (peers.length < 2) continue;

    const shadows = peers.map((p) => {
      const s = p.styleObj?.shadow;
      if (!s || (s.x === 0 && s.y === 0 && s.b === 0 && s.s === 0)) return { kuikaId: p.kuikaId, key: "none" };
      return { kuikaId: p.kuikaId, key: `${s.x},${s.y},${s.b},${s.s},${s.color || ""}` };
    });

    const uniqueShadows = new Set(shadows.map((s) => s.key));
    if (uniqueShadows.size > 1) {
      // Find most common shadow
      const shadowCounts = new Map<string, number>();
      for (const s of shadows) {
        shadowCounts.set(s.key, (shadowCounts.get(s.key) || 0) + 1);
      }
      let bestShadow = "";
      let bestCount = 0;
      for (const [key, count] of shadowCounts) {
        if (count > bestCount) {
          bestShadow = key;
          bestCount = count;
        }
      }

      const nonConforming = shadows.filter((s) => s.key !== bestShadow).map((s) => s.kuikaId);

      if (nonConforming.length > 0 && bestShadow !== "none") {
        const [x, y, b, s, color] = bestShadow.split(",");
        issues.push({
          category: "shadows",
          severity: "info",
          issue: "inconsistent_shadows",
          components: nonConforming,
          suggestion: "Normalize panel shadows for visual consistency"
        });

        fixes.push({
          description: "Normalize panel shadows",
          components: nonConforming,
          styles: {
            shadowX: Number(x),
            shadowY: Number(y),
            shadowBlur: Number(b),
            shadowSpread: Number(s),
            ...(color ? { shadowColor: color } : {})
          }
        });
      }
    }
  }

  return { issues, fixes };
}

function checkColorHarmony(components: ComponentInfo[]): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  const textColors = new Set<string>();
  const fillColors = new Set<string>();

  for (const comp of components) {
    const textColor = comp.styleObj?.text?.color;
    if (textColor && textColor !== "transparent") textColors.add(textColor);
    const fillColor = comp.styleObj?.fill?.color;
    if (fillColor && fillColor !== "transparent") fillColors.add(fillColor);
  }

  const totalUniqueColors = new Set([...textColors, ...fillColors]).size;
  if (totalUniqueColors > 8) {
    issues.push({
      category: "colors",
      severity: "info",
      issue: "too_many_colors",
      components: [],
      details: { count: totalUniqueColors, textColors: [...textColors], fillColors: [...fillColors] },
      suggestion: `Using ${totalUniqueColors} unique colors. Consider consolidating to 5-6 colors for a cohesive palette.`
    });
  }

  return { issues, fixes };
}

function checkMissingContent(components: ComponentInfo[]): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  for (const comp of components) {
    const props = comp.properties;
    if (!props || !Array.isArray(props)) continue;

    if (comp.name === "Label") {
      const valueProp = props.find((p: any) => p.name === "value");
      const currentValue = valueProp?.symbol?.fixedValue ?? valueProp?.value;
      if (currentValue === "label" || currentValue === "Label" || currentValue == null) {
        issues.push({
          category: "properties",
          severity: "warning",
          issue: "default_content",
          components: [comp.kuikaId],
          details: { property: "value", currentValue: currentValue || "empty" },
          suggestion: `Label ${comp.friendlyName || comp.kuikaId} has default text "${currentValue || "empty"}". Set a meaningful value.`
        });
      }
    }

    if (comp.name === "Button") {
      const labelProp = props.find((p: any) => p.name === "label");
      const currentValue = labelProp?.symbol?.fixedValue ?? labelProp?.value;
      if (currentValue === "Button text" || currentValue === "Button" || currentValue == null) {
        issues.push({
          category: "properties",
          severity: "warning",
          issue: "default_content",
          components: [comp.kuikaId],
          details: { property: "label", currentValue: currentValue || "empty" },
          suggestion: `Button ${comp.friendlyName || comp.kuikaId} has default text "${currentValue || "empty"}". Set a meaningful label.`
        });
      }
    }

    if (comp.name === "Image" || comp.name === "Thumbnail") {
      const valueProp = props.find((p: any) => p.name === "value");
      const isBound = valueProp?.symbol?.category != null && valueProp.symbol.category !== 0; // 0 = fixed
      if (!isBound) {
        const currentValue = valueProp?.symbol?.fixedValue ?? valueProp?.value;
        if (!currentValue || currentValue === "") {
          issues.push({
            category: "properties",
            severity: "warning",
            issue: "missing_image_url",
            components: [comp.kuikaId],
            details: { property: "value", currentValue: "empty" },
            suggestion: `${comp.name} ${comp.friendlyName || comp.kuikaId} has no image URL set. Set the "value" property to an image URL or bind it to a data source.`
          });
        }
      }
    }
  }

  return { issues, fixes };
}

function isLargeText(comp: ComponentInfo): boolean {
  const fontSize = comp.styleObj?.text?.fontSize;
  if (fontSize == null) return false;
  if (fontSize >= 18) return true;
  if (fontSize >= 14) {
    const fontWeight = comp.styleObj?.text?.fontWeight;
    return fontWeight === "bold" || fontWeight === "bolder" || (typeof fontWeight === "number" && fontWeight >= 700);
  }
  return false;
}

// Structural components that don't render text directly — skip contrast
const STRUCTURAL_SKIP_SET = new Set([
  "Row",
  "Col",
  "Column",
  "TableHeader",
  "TableRow",
  "TableDefaultColumn",
  "TableTemplateColumn",
  "Form",
  "VerticalStack",
  "HorizontalStack",
  "Body",
  "Header",
  "Footer"
]);

function checkAccessibility(
  components: ComponentInfo[],
  dictionary?: Record<number, any>
): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  for (const comp of components) {
    // Skip structural components that don't render text directly
    if (STRUCTURAL_SKIP_SET.has(comp.name)) continue;

    const textColor = comp.styleObj?.text?.color;
    const bgColor = comp.styleObj?.fill?.color;

    if (textColor && textColor !== "transparent") {
      // Determine effective background: if component bg is absent, transparent, or translucent,
      // resolve by compositing parent backgrounds using the full dictionary for complete parent chain
      let effectiveBg: string | null = null;

      if (!bgColor || bgColor === "transparent" || isTranslucent(bgColor)) {
        effectiveBg = resolveEffectiveBackground(comp.kuikaId, components, bgColor, dictionary);
      } else {
        effectiveBg = bgColor;
      }

      if (effectiveBg) {
        const largeText = isLargeText(comp);
        const requiredRatio = largeText ? 3 : 4.5;
        const ratio = getContrastRatio(textColor, effectiveBg);

        if (ratio < requiredRatio) {
          const roundedRatio = Math.round(ratio * 100) / 100;

          issues.push({
            category: "accessibility",
            severity: "error",
            issue: "low_contrast",
            components: [comp.kuikaId],
            details: {
              textColor,
              bgColor: effectiveBg,
              ...(bgColor !== effectiveBg ? { originalBgColor: bgColor || "none" } : {}),
              ratio: roundedRatio,
              largeText,
              requiredRatio
            },
            suggestion: `Text contrast ratio ${ratio.toFixed(1)}:1 on ${comp.friendlyName || comp.name} is below WCAG AA minimum (${requiredRatio}:1${largeText ? " for large text" : ""})`
          });

          // Generate quickFix with adjusted foreground color
          const fixedColor = findAccessibleForeground(textColor, effectiveBg, requiredRatio);
          if (fixedColor) {
            const newRatio = getContrastRatio(fixedColor, effectiveBg);
            const roundedNewRatio = Math.round(newRatio * 100) / 100;
            fixes.push({
              description: `Fix contrast: ${textColor} on ${effectiveBg} (${roundedRatio}:1 → ${roundedNewRatio}:1)`,
              components: [comp.kuikaId],
              styles: { color: fixedColor },
              accessibilityInfo: {
                oldRatio: roundedRatio,
                newRatio: roundedNewRatio,
                standard: "WCAG AA"
              }
            });
          }
        }
      }
    }

    // Check small text
    const fontSize = comp.styleObj?.text?.fontSize;
    if (fontSize != null && fontSize < 12 && (comp.categoryGroup === "display" || comp.name === "Label")) {
      // Already covered by typography check — skip duplicate
    }
  }

  return { issues, fixes };
}

function checkResponsiveBreakpoints(
  components: ComponentInfo[],
  sd: any
): { issues: ReviewIssue[]; fixes: QuickFix[] } {
  const issues: ReviewIssue[] = [];
  const fixes: QuickFix[] = [];

  const rows = components.filter((c) => c.name === "Row");

  for (const row of rows) {
    const dict = sd.screenProvider.state.screenDesignDictionary;
    const rowDi = dict[parseInt(row.kuikaId, 10)];
    if (!rowDi) continue;

    const colChildren = (rowDi.children || []).filter((c: any) => c.item?.name === "Col" || c.item?.name === "Column");

    if (colChildren.length < 2) continue;

    // Check if the Row has different mobile/tablet breakpoints
    // Columns store responsive data in item.properties as "responsiveXS", "responsiveSM" etc.
    // or in the row's responsive column fractions stored by the designer
    const hasResponsiveOverride = colChildren.some((col: any) => {
      const props = col.item?.properties || [];
      const xs = props.find((p: any) => p.name === "xs");
      const sm = props.find((p: any) => p.name === "sm");
      const xsVal = xs?.symbol?.fixedValue ?? xs?.value;
      const smVal = sm?.symbol?.fixedValue ?? sm?.value;
      // If xs or sm has a value different from the desktop span, it has a responsive override
      return (xsVal != null && xsVal !== "") || (smVal != null && smVal !== "");
    });

    if (!hasResponsiveOverride) {
      issues.push({
        category: "responsive",
        severity: "warning",
        issue: "missing_mobile_breakpoints",
        components: [row.kuikaId],
        details: { columnCount: colChildren.length },
        suggestion: `Row ${row.friendlyName || row.kuikaId} has ${colChildren.length} columns but no mobile/tablet breakpoint overrides. Columns will not stack on small screens.`
      });

      fixes.push({
        description: `Set mobile columns to 1 (stack) for Row ${row.friendlyName || row.kuikaId}`,
        components: [row.kuikaId],
        properties: [{ propertyName: "mobileColumns", value: "1" }]
      });
    }
  }

  return { issues, fixes };
}

function calculateScore(issues: ReviewIssue[]): number {
  let score = 100;
  for (const issue of issues) {
    switch (issue.severity) {
      case "error":
        score -= 10;
        break;
      case "warning":
        score -= 5;
        break;
      case "info":
        score -= 2;
        break;
    }
  }
  return Math.max(0, Math.min(100, score));
}

export const reviewTools: ToolHandlerGroup = {
  sync: {
    ui_style_review_screen_design: (input) => {
      const scope = input.scope || "full";
      const categories = safeParse(input.categories);

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;

      let rootDi: any;
      if (scope === "section" && input.kuikaId) {
        rootDi = dict[parseInt(input.kuikaId, 10)];
        if (!rootDi) return { error: `Component "${input.kuikaId}" not found` };
      } else {
        // Full screen — start from Body (id = -1)
        rootDi = dict[-1];
        if (!rootDi) return { error: "Screen design not found" };
      }

      // Collect all components
      const components: ComponentInfo[] = [];
      collectComponents(rootDi, sd, 0, components);

      // Also collect Header and Footer for full scope
      if (scope === "full") {
        const headerDi = dict[-2];
        if (headerDi) {
          collectComponents(headerDi, sd, 0, components);
        }
        const footerDi = dict[-3];
        if (footerDi) {
          collectComponents(footerDi, sd, 0, components);
        }
      }

      const allCategories = ["spacing", "typography", "colors", "shadows", "properties", "accessibility", "responsive"];
      const activeCategories =
        categories && Array.isArray(categories) && categories.length > 0 ? categories : allCategories;

      const allIssues: ReviewIssue[] = [];
      const allFixes: QuickFix[] = [];

      for (const cat of activeCategories) {
        let result: { issues: ReviewIssue[]; fixes: QuickFix[] };
        switch (cat) {
          case "spacing":
            result = checkSpacingConsistency(components);
            break;
          case "typography":
            result = checkTypography(components);
            break;
          case "colors":
            result = checkColorHarmony(components);
            break;
          case "shadows":
            result = checkShadowConsistency(components);
            break;
          case "properties":
            result = checkMissingContent(components);
            break;
          case "accessibility":
            result = checkAccessibility(components, dict);
            break;
          case "responsive":
            result = checkResponsiveBreakpoints(components, sd);
            break;
          default:
            continue;
        }
        allIssues.push(...result.issues);
        allFixes.push(...result.fixes);
      }

      const score = calculateScore(allIssues);
      const errorCount = allIssues.filter((i) => i.severity === "error").length;
      const warningCount = allIssues.filter((i) => i.severity === "warning").length;
      const infoCount = allIssues.filter((i) => i.severity === "info").length;

      const summaryParts: string[] = [];
      if (errorCount > 0) summaryParts.push(`${errorCount} error${errorCount > 1 ? "s" : ""}`);
      if (warningCount > 0) summaryParts.push(`${warningCount} warning${warningCount > 1 ? "s" : ""}`);
      if (infoCount > 0) summaryParts.push(`${infoCount} suggestion${infoCount > 1 ? "s" : ""}`);

      return {
        success: true,
        score,
        componentCount: components.length,
        summary:
          allIssues.length === 0
            ? "No issues found. Design looks clean!"
            : `Found ${summaryParts.join(", ")} across ${components.length} components.`,
        quickFixes: allFixes,
        issues: allIssues
      };
    },

    ui_style_apply_design_fixes: (input) => {
      const fixes = safeParse(input.fixes);
      if (!fixes || !Array.isArray(fixes) || fixes.length === 0) {
        return { error: "fixes array is required and must not be empty" };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const results: any[] = [];

      for (const fix of fixes) {
        const { components: componentIds, styles, properties } = fix;
        if (!componentIds || !Array.isArray(componentIds) || componentIds.length === 0) {
          results.push({ error: "Each fix must have a components array", fix });
          continue;
        }

        for (const kuikaId of componentIds) {
          try {
            const di = dict[parseInt(kuikaId, 10)];
            if (!di) {
              results.push({ error: `Component "${kuikaId}" not found`, kuikaId });
              continue;
            }

            let stylesApplied = false;
            let propsApplied = false;

            if (styles && typeof styles === "object" && Object.keys(styles).length > 0) {
              applyCssStylesToComponent(sd, di, kuikaId, styles);
              stylesApplied = true;
            }

            if (properties && Array.isArray(properties)) {
              for (const prop of properties) {
                const { propertyName, value } = prop;
                if (!propertyName || value === undefined) continue;
                applyDesignerPropertyChange(sd, { propertyName, value, kuikaId });
              }
              propsApplied = true;
            }

            results.push({
              success: true,
              kuikaId,
              stylesApplied,
              propertiesApplied: propsApplied
            });
          } catch (e: any) {
            results.push({ error: e.message, kuikaId });
          }
        }
      }

      return {
        success: true,
        fixesApplied: fixes.length,
        componentsUpdated: results.filter((r) => r.success).length,
        results
      };
    }
  }
};
